/**
 * Map file schema.
 *
 * Geometry is optional: the continent-grouped list view needs only the graph,
 * so a map is usable long before anyone draws it. `mapHasGeometry` reports
 * whether a map can also be rendered on the SVG map view.
 */

import { z } from 'zod';

const identifier = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_]+$/, 'ids must be lowercase alphanumeric with underscores');

const point = z.object({ x: z.number(), y: z.number() });

export const continentSchema = z.object({
  id: identifier,
  name: z.string().min(1).max(64),
  bonus: z.number().int().min(0).max(100),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'color must be a #rrggbb hex string'),
  territories: z.array(identifier).min(1),
});

export const territorySchema = z.object({
  id: identifier,
  name: z.string().min(1).max(64),
  /** Abbreviation for the map, where a full name will not fit its shape. */
  short: z.string().min(1).max(24).optional(),
  continent: identifier,
  adjacent: z.array(identifier),
  capital: z.boolean().optional(),
  /** SVG path data. Present only once the map has been drawn. */
  path: z.string().min(1).optional(),
  label: point.optional(),
  badge: point.optional(),
  /** Enlarged circular tap target for territories too small to hit on a phone. */
  hit: z.object({ cx: z.number(), cy: z.number(), r: z.number().positive() }).optional(),
});

export const riskMapSchema = z.object({
  formatVersion: z.literal(1),
  id: identifier,
  name: z.string().min(1).max(120),
  version: z.string().min(1).max(32),
  license: z.string().max(120).optional(),
  author: z.string().max(120).optional(),
  /** `[minX, minY, width, height]`. Required for the map view. */
  viewBox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
  continents: z.array(continentSchema).min(1),
  territories: z.array(territorySchema).min(2),
  connections: z
    .array(z.object({ a: identifier, b: identifier, style: z.string().max(32).optional() }))
    .optional(),
});

export type Continent = z.infer<typeof continentSchema>;
export type Territory = z.infer<typeof territorySchema>;
export type RiskMap = z.infer<typeof riskMapSchema>;

/** True when every territory carries geometry and the map declares a viewBox. */
export function mapHasGeometry(map: RiskMap): boolean {
  return map.viewBox !== undefined && map.territories.every((t) => t.path !== undefined);
}
